import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaTags, FaCheck, FaTimes, FaFilter, FaCopy } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { formatDate } from '../../utils/helpers';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';

const DiscountCodeManagement = () => {
  const { showToast } = useToast();
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  const initialFormState = {
    code: '',
    description: '',
    discountPercentage: 10,
    type: 'subscription',
    applicableFor: {
      subscriptionTiers: ['essential', 'growth', 'plus'],
      allProducts: false,
      minimumAmount: 0
    },
    expiresAt: new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().split('T')[0],
    usageLimit: null,
    isActive: true
  };
  
  const [formData, setFormData] = useState(initialFormState);
  
  useEffect(() => {
    fetchDiscountCodes();
  }, []);
  
  const fetchDiscountCodes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/discounts?type=subscription');
      setDiscountCodes(response.data.data);
    } catch (error) {
      console.error('Error fetching discount codes:', error);
      showToast('Failed to load discount codes', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleFilterChange = (e) => {
    setFilterType(e.target.value);
  };
  
  const filteredDiscountCodes = discountCodes.filter(code => {
    const matchesSearch = code.code.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         code.description.toLowerCase().includes(searchTerm.toLowerCase());
                         
    const matchesFilter = filterType === 'all' || 
                         (filterType === 'active' && code.isActive) || 
                         (filterType === 'inactive' && !code.isActive);
    
    return matchesSearch && matchesFilter;
  });
  
  const openModal = (code = null) => {
    if (code) {
      // Edit mode - load the selected code
      const expiryDate = new Date(code.expiresAt).toISOString().split('T')[0];
      
      setFormData({
        code: code.code,
        description: code.description,
        discountPercentage: code.discountPercentage,
        type: 'subscription',
        applicableFor: {
          subscriptionTiers: code.applicableFor.subscriptionTiers || ['essential', 'growth', 'plus'],
          allProducts: code.applicableFor.allProducts || false,
          minimumAmount: code.applicableFor.minimumAmount || 0
        },
        expiresAt: expiryDate,
        usageLimit: code.usageLimit,
        isActive: code.isActive
      });
      
      setSelectedCode(code);
    } else {
      // Add mode - reset to initial state with subscription type
      setFormData({
        ...initialFormState,
        code: generateRandomCode(),
        type: 'subscription'
      });
      setSelectedCode(null);
    }
    setModalOpen(true);
  };
  
  const closeModal = () => {
    setModalOpen(false);
    setSelectedCode(null);
  };
  
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('applicableFor.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        applicableFor: {
          ...prev.applicableFor,
          [field]: type === 'checkbox' ? checked : value
        }
      }));
    } else if (name === 'subscriptionTiers') {
      const tiers = Array.from(e.target.selectedOptions, option => option.value);
      setFormData(prev => ({
        ...prev,
        applicableFor: {
          ...prev.applicableFor,
          subscriptionTiers: tiers
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsFormSubmitting(true);
      
      // Format data for submission
      const submitData = {
        ...formData,
        type: 'subscription',
        usageLimit: formData.usageLimit === '' ? null : parseInt(formData.usageLimit),
        discountPercentage: parseInt(formData.discountPercentage),
        // Ensure proper structure for subscription discounts
        applicableFor: {
          subscriptionTiers: formData.applicableFor.subscriptionTiers,
          minimumAmount: parseInt(formData.applicableFor.minimumAmount) || 0,
          allProducts: false, // For subscription type, this is irrelevant
          products: [] // Explicitly set empty products array for subscription type
        }
      };
      
      console.log('Submitting discount code data:', submitData);
      
      if (selectedCode) {
        // Update existing discount code
        await api.put(`/discounts/${selectedCode._id}`, submitData);
        showToast('Discount code updated successfully', 'success');
      } else {
        // Create new discount code
        await api.post('/discounts', submitData);
        showToast('Discount code created successfully', 'success');
      }
      
      // Refresh the list
      fetchDiscountCodes();
      closeModal();
    } catch (error) {
      console.error('Error saving discount code:', error);
      
      const errorMessage = error.response?.data?.error || 
                          'Failed to save discount code. Please try again.';
      
      showToast(errorMessage, 'error');
    } finally {
      setIsFormSubmitting(false);
    }
  };
  
  const openDeleteConfirm = (code) => {
    setSelectedCode(code);
    setConfirmDeleteOpen(true);
  };
  
  const handleDelete = async () => {
    try {
      setIsFormSubmitting(true);
      await api.delete(`/discounts/${selectedCode._id}`);
      showToast('Discount code deleted successfully', 'success');
      
      // Refresh the list
      fetchDiscountCodes();
      setConfirmDeleteOpen(false);
    } catch (error) {
      console.error('Error deleting discount code:', error);
      showToast('Failed to delete discount code', 'error');
    } finally {
      setIsFormSubmitting(false);
    }
  };
  
  const generateRandomCode = () => {
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  };
  
  const handleGenerateNewCode = () => {
    setFormData(prev => ({
      ...prev,
      code: generateRandomCode()
    }));
  };
  
  const copyCodeToClipboard = (code) => {
    navigator.clipboard.writeText(code).then(
      () => {
        showToast('Discount code copied to clipboard', 'success');
      },
      () => {
        showToast('Failed to copy code', 'error');
      }
    );
  };
  
  const tierLabels = {
    'essential': 'Essential',
    'growth': 'Growth',
    'plus': 'Plus',
    'ignite': 'Ignite',
    'elevate': 'Elevate'
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Discount Codes Management</h1>
        <button
          onClick={() => openModal()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <FaPlus className="mr-2" /> Add New Discount Code
        </button>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white shadow-sm rounded-md p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search discount codes..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
          <div className="md:w-64">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaFilter className="text-gray-400" />
              </div>
              <select
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                value={filterType}
                onChange={handleFilterChange}
              >
                <option value="all">All Codes</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      
      {/* Discount Codes List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="text-center py-10">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredDiscountCodes.length === 0 ? (
          <div className="text-center py-10">
            <FaTags className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No discount codes found</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new discount code.</p>
            <div className="mt-6">
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => openModal()}
              >
                <FaPlus className="-ml-1 mr-2 h-5 w-5" />
                New Discount Code
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Code & Description
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Discount
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Applicable For
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Usage & Expiry
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDiscountCodes.map((discountCode) => (
                  <tr key={discountCode._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="flex items-center text-sm font-medium text-gray-900">
                            {discountCode.code}
                            <button 
                              onClick={() => copyCodeToClipboard(discountCode.code)}
                              className="ml-2 text-gray-400 hover:text-gray-600"
                              title="Copy to clipboard"
                            >
                              <FaCopy />
                            </button>
                          </div>
                          <div className="text-sm text-gray-500">{discountCode.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-medium">
                        {discountCode.discountPercentage}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {discountCode.applicableFor.subscriptionTiers.map(tier => (
                          <span
                            key={tier}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1"
                          >
                            {tierLabels[tier] || tier}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        Used: {discountCode.usedCount} {discountCode.usageLimit ? `/ ${discountCode.usageLimit}` : ''}
                      </div>
                      <div className="text-sm text-gray-500">
                        Expires: {formatDate(discountCode.expiresAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          discountCode.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {discountCode.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openModal(discountCode)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => openDeleteConfirm(discountCode)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Add/Edit Discount Code Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={selectedCode ? "Edit Discount Code" : "Add New Discount Code"}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                Code
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <input
                  type="text"
                  name="code"
                  id="code"
                  required
                  className="flex-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full min-w-0 rounded-md sm:text-sm border-gray-300"
                  value={formData.code}
                  onChange={handleFormChange}
                />
                <button
                  type="button"
                  className="ml-3 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={handleGenerateNewCode}
                >
                  Generate
                </button>
              </div>
            </div>
            
            <div className="sm:col-span-3">
              <label htmlFor="discountPercentage" className="block text-sm font-medium text-gray-700">
                Discount Percentage
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  type="number"
                  name="discountPercentage"
                  id="discountPercentage"
                  min="1"
                  max="100"
                  required
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-12 sm:text-sm border-gray-300 rounded-md"
                  value={formData.discountPercentage}
                  onChange={handleFormChange}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">%</span>
                </div>
              </div>
            </div>
            
            <div className="sm:col-span-6">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="description"
                  id="description"
                  required
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  value={formData.description}
                  onChange={handleFormChange}
                />
              </div>
            </div>
            
            <div className="sm:col-span-6">
              <label htmlFor="subscriptionTiers" className="block text-sm font-medium text-gray-700">
                Applicable Subscription Tiers
              </label>
              <div className="mt-1">
                <select
                  id="subscriptionTiers"
                  name="subscriptionTiers"
                  multiple
                  required
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  value={formData.applicableFor.subscriptionTiers}
                  onChange={handleFormChange}
                  size={5}
                >
                  <option value="essential">Essential</option>
                  <option value="growth">Growth</option>
                  <option value="plus">Plus</option>
                  <option value="ignite">Ignite (School)</option>
                  <option value="elevate">Elevate (School)</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Hold Ctrl/Cmd to select multiple tiers
                </p>
              </div>
            </div>
            
            <div className="sm:col-span-3">
              <label htmlFor="expiresAt" className="block text-sm font-medium text-gray-700">
                Expiration Date
              </label>
              <div className="mt-1">
                <input
                  type="date"
                  name="expiresAt"
                  id="expiresAt"
                  required
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  value={formData.expiresAt}
                  onChange={handleFormChange}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            
            <div className="sm:col-span-3">
              <label htmlFor="usageLimit" className="block text-sm font-medium text-gray-700">
                Usage Limit
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  name="usageLimit"
                  id="usageLimit"
                  min="1"
                  placeholder="Unlimited"
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  value={formData.usageLimit === null ? '' : formData.usageLimit}
                  onChange={handleFormChange}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Leave empty for unlimited uses
                </p>
              </div>
            </div>
            
            <div className="sm:col-span-6">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="isActive"
                    name="isActive"
                    type="checkbox"
                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    checked={formData.isActive}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="isActive" className="font-medium text-gray-700">
                    Active
                  </label>
                  <p className="text-gray-500">
                    Inactive codes cannot be used even if they are valid
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={closeModal}
              disabled={isFormSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              disabled={isFormSubmitting}
            >
              {isFormSubmitting ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  {selectedCode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                selectedCode ? 'Update Discount Code' : 'Create Discount Code'
              )}
            </button>
          </div>
        </form>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        title="Delete Discount Code"
        size="sm"
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete the discount code <span className="font-bold">{selectedCode ? selectedCode.code : ''}</span>? This action cannot be undone.
          </p>
        </div>
        
        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={() => setConfirmDeleteOpen(false)}
            disabled={isFormSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            onClick={handleDelete}
            disabled={isFormSubmitting}
          >
            {isFormSubmitting ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default DiscountCodeManagement; 